import { Statistic } from '@helpers/statistics.ts';
import {
  algorithms,
  calculation_mode,
  ChangeInputEvent,
  IStation,
  IVehiclesCapacities,
  sizes_enum,
  universe_mode,
  vehicle_types,
  Version,
} from '@helpers/types.ts';
import { useHttp } from '@hooks/use-http';
import { useLogin } from '@hooks/use-login.ts';
import { useCallback, useState } from 'react';
import {
  adjectives,
  animals,
  colors,
  Config,
  uniqueNamesGenerator,
} from 'unique-names-generator';

type Input = ChangeInputEvent;
export const useConfigurePlan = (station: IStation | null) => {
  const http = useHttp();
  const [preloadPlan, setPreloadPlan] = useState<boolean>(false);
  const { email, name } = useLogin();
  const [simulatingPlan, setSimulatingPlan] = useState<boolean>(false);
  const [plan_id, setPlanId] = useState<string>('');
  const [plan_name, setPlanName] = useState<string>('');
  const [prefix, setPrefix] = useState<string>('');
  const [mode, setMode] = useState<calculation_mode>(calculation_mode.manual);
  const [universe, setUniverseMode] = useState<Array<universe_mode>>([
    universe_mode.station,
    //universe_mode.station_return,
    universe_mode.linehaul,
    //universe_mode.linehaul_return,
  ]);
  const [sizes, setSizes] = useState<Array<sizes_enum>>([
    sizes_enum.xs,
    sizes_enum.sm,
    sizes_enum.md,
    sizes_enum.lg,
    sizes_enum.xl,
  ]);
  const [version, setVersion] = useState<Version | null>(null);
  const [vehicles, setVehicles] = useState<Partial<IVehiclesCapacities>>({
    [vehicle_types.motorcycle]: 0,
    [vehicle_types.auto]: 0,
    [vehicle_types.small_van]: 0,
    [vehicle_types.large_van]: 0,
  });
  const [metrics, setMetrics] = useState<Statistic | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [simulationTime, setSimulationTime] = useState<number>(300);
  const [routeTime, setRouteTime] = useState<number>(9);
  const [service_time, setServiceTime] = useState<number>(180);
  const [max_distance, setMaxDistance] = useState<number>(100000);
  const [preset, setPreset] = useState<string>('small_parcel');
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [fusion, setFusion] = useState<number>(0);
  const [algorithm, setAlgorithm] = useState<algorithms>(algorithms.or_tools);
  const onChangePlanName = useCallback(({ target: { value } }: Input) => {
    setPlanName(value);
  }, []);
  const onChangePrefix = useCallback(({ target: { value } }: Input) => {
    setPrefix(value.toUpperCase().substring(0, 10));
  }, []);
  const onChangeMode = useCallback((value: calculation_mode) => {
    setMode(value);
  }, []);
  const onChangeUniverseMode = useCallback((value: Array<universe_mode>) => {
    setMetrics(null);
    setUniverseMode(value);
  }, []);
  const onChangeSizes = useCallback((value: Array<sizes_enum>) => {
    setMetrics(null);
    setSizes(value);
  }, []);
  const onChangeVersion = useCallback((value: Version) => {
    setVersion(value);
    setMetrics(null);
  }, []);

  const onChangeVehicles = useCallback(
    (newVehicles: Partial<IVehiclesCapacities>) => {
      setMetrics(null);
      setVehicles((prev) => ({ ...prev, ...newVehicles }));
    },
    [],
  );

  const onChangeFile = useCallback((file: File) => {
    setMetrics(null);
    setFile(file);
  }, []);

  const onChangeSimulationTime = useCallback((value: number) => {
    setMetrics(null);
    setSimulationTime(value);
  }, []);

  const onChangeRouteTime = useCallback((value: number) => {
    setMetrics(null);
    setRouteTime(value);
  }, []);

  const onChangeServiceTime = useCallback((value: number) => {
    setMetrics(null);
    setServiceTime(value);
  }, []);

  const onChangePreset = useCallback((value: string) => {
    setMetrics(null);
    setPreset(value);
  }, []);

  const onChangeMaxDistance = useCallback((value: number) => {
    setMetrics(null);
    setMaxDistance(value);
  }, []);

  const onChangeSimulating = useCallback((value: boolean) => {
    setSimulatingPlan(value);
  }, []);

  const onChangeFusion = useCallback(
    (_event: Event, newValue: number | number[]) => {
      setMetrics(null);
      setFusion(newValue as number);
    },
    [],
  );

  const onRandomizeName = useCallback(() => {
    const config: Config = {
      dictionaries: [adjectives, colors, animals],
      length: 3,
      separator: '-',
    };

    const characterName: string = uniqueNamesGenerator(config);
    onChangePlanName({ target: { value: characterName } } as any);
    const prefixMatch = characterName
      .split('-')
      .join(' ')
      .match(/\b([A-Za-z0-9])/g);

    const prefix = prefixMatch ? prefixMatch.join('').substring(0, 1) : '';
    const random = Math.floor(Math.random() * 10);

    onChangePrefix({
      target: {
        value: `${prefix}${random}`,
      },
    } as any);
  }, [onChangePlanName, onChangePrefix]);

  const onChangeAlgorithm = useCallback((value: algorithms) => {
    setMetrics(null);
    setAlgorithm(value);
  }, []);

  const isValidVehicles = useCallback(() => {
    return (
      (vehicles[vehicle_types.motorcycle] || 0) +
        (vehicles[vehicle_types.auto] || 0) +
        (vehicles[vehicle_types.small_van] || 0) +
        (vehicles[vehicle_types.large_van] || 0) >
      0
    );
  }, [vehicles]);

  const onVerifyPlan = useCallback(async () => {
    if (!station || !version || !file) {
      setError('Faltan datos para la verificación del plan.');
      return;
    }
    try {
      setError(null);
      setMetrics(null);
      setPreloadPlan(true);
      setLoadingMessage(
        'Espere un momento, estamos verificando el universo de ordenes',
      );

      const m = await http.lastmile.getRoutingVerification(
        plan_id,
        plan_name,
        prefix,
        station.station,
        station.country,
        mode,
        universe,
        sizes,
        version,
        vehicles,
        file,
        simulationTime,
        routeTime,
        fusion,
        algorithm,
        max_distance,
        service_time,
        preset,
      );

      setMetrics(m);
      setPlanId(m.plan_id);
    } catch (error: any) {
      setError(error.response?.data?.message || error.message);
    } finally {
      setLoadingMessage('');
      setPreloadPlan(false);
    }
  }, [
    plan_id,
    plan_name,
    prefix,
    mode,
    station,
    universe,
    sizes,
    version,
    vehicles,
    file,
    simulationTime,
    routeTime,
    service_time,
    fusion,
    algorithm,
    max_distance,
    preset,
    http,
  ]);

  const onStartSimulation = useCallback(
    async (
      plan_id: string,
      email: string,
      name: string,
      algorithm: algorithms,
    ) => {
      try {
        setLoadingMessage(
          `Simulación iniciada. Por favor, no recargue el sitio hastá finalizar; Este proceso puede tardar varios minutos`,
        );
        if (algorithm == algorithms.k_means) {
          await http.lastmile.startKSimulation(plan_id, `${email} | ${name}`);
        } else {
          await http.lastmile.startSimulation(plan_id, `${email} | ${name}`);
        }

        window.location.href = `/simulation/${plan_id}`;
      } catch (error: any) {
        setError(error.response?.data?.message || error.message);
      } finally {
        setLoadingMessage('');
      }
    },
    [http],
  );

  const onPrepareSimulation = useCallback(async () => {
    if (!plan_id || !email || !name || !algorithm) {
      setError('Faltan datos para iniciar la simulación.');
      return;
    }
    setError(null);
    onChangeSimulating(true);
    setLoadingMessage(
      `Espere un momento, estamos preparando todo para la simulación.\nPor favor, no recargue el sitio hasta finalizar`,
    );
    try {
      // cuando es kmeans no requiere que prepare nada
      if (algorithm != algorithms.k_means) {
        await http.lastmile.prepareSimulation(plan_id);
      }
      await onStartSimulation(plan_id, email, name, algorithm);
    } catch (error: any) {
      setError(error.response?.data?.message || error.message);
    } finally {
      onChangeSimulating(false);
      setLoadingMessage('');
    }
  }, [plan_id, email, name, algorithm, http, onStartSimulation]);

  return {
    preloadPlan,
    simulatingPlan,
    plan_name,
    prefix,
    mode,
    universe,
    sizes,
    version,
    file,
    metrics,
    vehicles,
    fusion,
    error,
    algorithm,
    loadingMessage,
    simulationTime,
    routeTime,
    service_time,
    max_distance,
    preset,
    onChangePlanName,
    onChangePrefix,
    onChangeMode,
    onChangeUniverseMode,
    onChangeSizes,
    onChangeVersion,
    onVerifyPlan,
    onChangeFile,
    onChangeVehicles,
    isValidVehicles,
    onPrepareSimulation,
    onChangeSimulationTime,
    onChangeRouteTime,
    onChangeServiceTime,
    onChangeMaxDistance,
    onChangePreset,
    onRandomizeName,
    onChangeFusion,
    onChangeAlgorithm,
  };
};
